import AlertIcon from '@assets/icons/alert.svg';
import ChevronDownThin from '@assets/icons/chevron-down-thin.svg';
import i18n from '@/utils/i18n';
import moment from 'moment-timezone';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  adjustMomentForTimezone,
  formatDate,
  formatDateWithYear,
  getFulfillmentDateInRestaurantTimezone,
} from '@/utils/date';
import { Analytics } from '@/utils/analytics';
import { captureException } from '@/utils/sentry';
import { createStyles } from '@/contexts/theme-context';
import {
  CursorValue,
  Pressable,
  TextInput as RNTextInput,
  StyleProp,
  View,
  ViewStyle,
} from 'react-native';
import { FILTER_OPEN_STATUSES } from './order-inbounds-order-list/types';
import { FulfillmentActionSheet } from '@/components/partners/fulfillment-action-sheet';
import { FulfillmentName } from '@/types';
import { getOrderSummaryInputError } from '@/utils/new-order';
import { getTranslationValue } from '@/utils/translations';
import {
  InboundDetailListCustomerAccountPicker,
  type Props as InboundDetailListCustomerAccountPickerProps,
} from './inbound-detail-list-customer-account-picker';
import { InputErrorMap } from '@/components/orders/order-summary-inputs';
import { OrderAgentWorkflowMode, OrderMetadata } from './types';
import { OrderSummaryInput } from '@/contexts/dynamic-config';
import { OrderSummaryInputID, PoNumberDisplay, ViewableBy } from '@/enums';
import { SelectedFulfillmentType } from '@/contexts/new-order-actions-context-types';
import { Typography as SkTypography } from '@usepepper/sprout-kit-ui';
import { TextInput } from '@/components/text-input';
import { translateFulfillmentType } from '@domain/fulfillment-settings/utils/translate-fulfillment-type';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useDebouncedCallback } from 'use-debounce';
import { useIsMounted } from '@/hooks/use-is-mounted';
import { useLayoutContext } from '@/contexts/layout-context';
import { useNewOrderActionsContext } from '@/contexts/new-order-actions-context';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useNewOrderLoadingContext } from '@/contexts/new-order-loading-context';
import { useOrderInbounds } from '@/hooks/use-order-inbounds';
import {
  useOrderSummaryConfig,
  useSupplierDynamicConfigFlag,
  useSupplierDynamicConfigValue,
} from '@/hooks/use-dynamic-config-value';
import { useSupplierOrderAvailability } from '@/hooks/use-supplier-order-availability';
import { useToasts } from '@/contexts/toast-context';
import {
  useUpdateInboundFulfillmentOptions,
  useUpdateInboundOrderNotes,
  useUpdateInboundPoNumber,
} from './queries';

const INPUT_DEBOUNCE_DELAY_MS = 250;

// Two columns fit at a >= 700px panel per the designs: (668 - 16 gutter) / 2.
const FIELD_MIN_BASIS = 326;

export interface Props {
  accountPickerProps?: InboundDetailListCustomerAccountPickerProps;
  containerStyle?: StyleProp<ViewStyle>;
  onScrollToSelf?: () => void;
  orderMetadata: OrderMetadata[];
  workflowMode: OrderAgentWorkflowMode;
}

export interface InboundDetailListOrderOptionsRef {
  blur: () => void;
  scrollToSelf: () => void;
  validateAndFocus: () => boolean;
}

export const InboundDetailListOrderOptions = forwardRef<
  InboundDetailListOrderOptionsRef,
  Props
>(
  (
    {
      containerStyle,
      onScrollToSelf,
      orderMetadata,
      accountPickerProps,
      workflowMode,
    },
    ref,
  ) => {
    const [styles, theme] = useStyles();
    const { isSmallLayout } = useLayoutContext();
    const { showErrorToast } = useToasts();
    const isMounted = useIsMounted();
    const { isViewOnly: isOrderInboundsViewOnly } = useOrderInbounds();

    const isCorrectionMode = workflowMode === OrderAgentWorkflowMode.Correction;
    const isViewOnly = isOrderInboundsViewOnly || isCorrectionMode;

    const fulfillmentDateInputRef = useRef<View>(null);
    const poNumberInputRef = useRef<RNTextInput>(null);
    const noteInputRef = useRef<RNTextInput>(null);
    const isInitialMountRef = useRef(true);

    const {
      supplierEmployeeRole,
      selectedRestaurantRelationship: { timezone: restaurantTimezone },
      user,
    } = useAuthenticatedUserContext();

    const { poNumberDisplay, selectableFulfillmentOptions, showOrderNotes } =
      useNewOrderContext();

    const orderSummaryConfig = useOrderSummaryConfig();

    const additionalOrderValidationRolesConfig = useSupplierDynamicConfigValue(
      'additional_order_validation_supplier_roles',
    );

    const isSurfaceNotesEnabled = useSupplierDynamicConfigFlag(
      'smart_order_config',
      'surface_notes_enabled',
    );

    const {
      setNextFulfillment,
      selectedFulfillment,
      additionalInputValueMap,
      setAdditionalInputValue,
    } = useNewOrderActionsContext();
    const { isUpdatingQuantity } = useNewOrderLoadingContext();

    const initialPoNumber = useRef<string | undefined>(undefined);
    const orderAvailability = useSupplierOrderAvailability();

    const [showFulfillmentSelector, setShowFulfillmentSelector] =
      useState(false);
    const [additionalInputErrorMap, setAdditionalInputErrorMap] =
      useState<InputErrorMap>({});

    const [
      updateInboundFulfillmentOptions,
      { loading: isUpdatingInboundFulfillmentOptions },
    ] = useUpdateInboundFulfillmentOptions();
    const [updateInboundPoNumber] = useUpdateInboundPoNumber();
    const [updateInboundOrderNotes] = useUpdateInboundOrderNotes();

    const onSaveFulfillment = async (
      nextSelectedFulfillment: SelectedFulfillmentType,
    ) => {
      if (
        orderAvailability.isValidSupplierFulfillmentDate(
          moment(nextSelectedFulfillment.date),
          supplierEmployeeRole,
          additionalOrderValidationRolesConfig,
        )
      ) {
        if (!latestOpenSmartOrderUUID) {
          return;
        }

        try {
          await setNextFulfillment(nextSelectedFulfillment);
          const utcStartOfDay = getFulfillmentDateInRestaurantTimezone(
            nextSelectedFulfillment.date,
            restaurantTimezone,
          );

          await updateInboundFulfillmentOptions({
            variables: {
              fulfillment_at: utcStartOfDay.toISOString(),
              fulfillment_type: nextSelectedFulfillment.type,
              openStatuses: [...FILTER_OPEN_STATUSES],
              updatedAt: new Date().toISOString(),
              updatedBy: user.uuid,
              uuid: latestOpenSmartOrderUUID,
            },
          });
        } catch (err) {
          captureException(err);
          showErrorToast();
        }
      }
    };

    const poNumber = useMemo(() => {
      return orderMetadata[0].poNumber ?? '';
    }, [orderMetadata]);

    const orderNotes = orderMetadata[0].orderNotes ?? '';

    const openSmartOrderUUIDs = useMemo(
      () =>
        orderMetadata
          .filter((order) => FILTER_OPEN_STATUSES.has(order.status))
          .map((order) => order.smartOrderUUID),
      [orderMetadata],
    );

    // we choose to only update the latest open order's updated_at.
    //
    // it's not strictly enforced yet on the backend but any smart order that is already "closed"
    // should be ignored when trying to update the updated_at column. right now, we use the
    // updated_at as a way to group orders together in the inbound list so that they appear
    // together if they are rejected at the same time. so we have to make sure we're not
    // unnecessarily updating the field if it's already in a closed status
    const latestOpenSmartOrderUUID = useMemo(() => {
      const latestOpenOrder = orderMetadata
        .filter((order) => FILTER_OPEN_STATUSES.has(order.status))
        .reduce<OrderMetadata | null>(
          (latest, order) =>
            !latest || order.createdAt > latest.createdAt ? order : latest,
          null,
        );
      return latestOpenOrder?.smartOrderUUID ?? null;
    }, [orderMetadata]);

    const lastPersistedNoteRef = useRef<string>(orderNotes);

    const customPOInput = orderSummaryConfig?.additional_inputs?.find(
      (input) => input.id === OrderSummaryInputID.PONumber,
    );

    const customNoteInput = orderSummaryConfig?.additional_inputs?.find(
      (input) => input.id === OrderSummaryInputID.Notes,
    );

    const resolveInputByViewability = useCallback(
      (input: OrderSummaryInput): OrderSummaryInput | null => {
        const isSupplierEmployee = Boolean(supplierEmployeeRole);
        switch (input.viewable_by) {
          case ViewableBy.ALL:
          case undefined: // Default to ALL for backward compatibility
            return input;
          case ViewableBy.SUPPLIER:
            return isSupplierEmployee ? input : null;
          case ViewableBy.RESTAURANT:
            // Order agent users are never restaurant employees, so
            // restaurant-only inputs never apply on this screen.
            return null;
          default:
            return input;
        }
      },
      [supplierEmployeeRole],
    );

    const poNumberInputConfig = useMemo((): OrderSummaryInput | null => {
      const additionalInputsConfig = orderSummaryConfig?.additional_inputs;
      if (additionalInputsConfig) {
        const poInput = additionalInputsConfig.find(
          (input) => input.id === OrderSummaryInputID.PONumber,
        );

        if (poInput) {
          return resolveInputByViewability(poInput);
        }
      }

      // Fallback: create default PO number input config
      if (poNumberDisplay !== PoNumberDisplay.Hide) {
        return {
          id: OrderSummaryInputID.PONumber,
          pattern: null,
          pattern_invalid_message: null,
          placeholder: null,
          required: poNumberDisplay === PoNumberDisplay.Required,
          subtitle: null,
          title: i18n.t('po_number'),
          type: 'text',
          viewable_by: ViewableBy.ALL,
        };
      }

      return null;
    }, [
      orderSummaryConfig?.additional_inputs,
      poNumberDisplay,
      resolveInputByViewability,
    ]);

    const orderNoteInputConfig = useMemo((): OrderSummaryInput | null => {
      if (!isSurfaceNotesEnabled) {
        return null;
      }

      const additionalInputsConfig = orderSummaryConfig?.additional_inputs;
      if (additionalInputsConfig) {
        const noteInput = additionalInputsConfig.find(
          (input) => input.id === OrderSummaryInputID.Notes,
        );

        return noteInput ? resolveInputByViewability(noteInput) : null;
      }

      // Fallback: mirror the Order Summary drawer's default notes gating.
      if (showOrderNotes) {
        return {
          id: OrderSummaryInputID.Notes,
          pattern: null,
          pattern_invalid_message: null,
          placeholder: null,
          required: false,
          subtitle: null,
          title: i18n.t('order_notes'),
          type: 'text',
          viewable_by: ViewableBy.ALL,
        };
      }

      return null;
    }, [
      isSurfaceNotesEnabled,
      orderSummaryConfig?.additional_inputs,
      resolveInputByViewability,
      showOrderNotes,
    ]);

    const updatePONumber = async (newPoNumber: string) => {
      if (!latestOpenSmartOrderUUID) {
        return;
      }

      const prevPoNumber = poNumber;

      try {
        await updateInboundPoNumber({
          variables: {
            openStatuses: [...FILTER_OPEN_STATUSES],
            po_number: newPoNumber,
            updatedAt: new Date().toISOString(),
            updatedBy: user.uuid,
            uuid: latestOpenSmartOrderUUID,
          },
        });

        Analytics.track('order_inbound_detail.po_number_updated', {
          newPoNumber,
          originalPoNumber: initialPoNumber.current,
          smartOrderUUIDs: orderMetadata.map((order) => order.smartOrderUUID),
        });
      } catch (err) {
        captureException(err);
        showErrorToast('Failed to update PO number');

        setAdditionalInputValue(
          OrderSummaryInputID.PONumber,
          i18n.t('po_number'),
          prevPoNumber,
        );
      }
    };

    const debouncedUpdatePONumber = useDebouncedCallback(
      updatePONumber,
      INPUT_DEBOUNCE_DELAY_MS,
    );

    const onSavePONumber = (newPoNumber: string) => {
      setAdditionalInputValue(
        OrderSummaryInputID.PONumber,
        i18n.t('po_number'),
        newPoNumber,
      );
      debouncedUpdatePONumber(newPoNumber);
    };

    const updateOrderNote = async (newOrderNote: string) => {
      const trimmedNote = newOrderNote.trim();

      if (
        orderNoteInputConfig &&
        getOrderSummaryInputError(orderNoteInputConfig, trimmedNote)
      ) {
        return;
      }

      if (
        openSmartOrderUUIDs.length === 0 ||
        trimmedNote === lastPersistedNoteRef.current
      ) {
        return;
      }

      try {
        await updateInboundOrderNotes({
          variables: {
            openStatuses: [...FILTER_OPEN_STATUSES],
            order_notes: trimmedNote,
            updatedAt: new Date().toISOString(),
            updatedBy: user.uuid,
            uuids: openSmartOrderUUIDs,
          },
        });

        lastPersistedNoteRef.current = trimmedNote;

        Analytics.track('order_inbound_detail.order_note_updated', {
          hasNote: Boolean(trimmedNote),
          noteLength: trimmedNote.length,
          smartOrderUUIDs: openSmartOrderUUIDs,
        });
      } catch (err) {
        captureException(err, {
          extra: {
            newOrderNote,
            smartOrderUUIDs: openSmartOrderUUIDs,
            trimmedNote,
          },
        });
        showErrorToast();

        // The input map lives in the stack-level context, so after an unmount
        // flush a rollback would overwrite the newly selected order's note.
        if (isMounted()) {
          setAdditionalInputValue(
            OrderSummaryInputID.Notes,
            i18n.t('order_notes'),
            lastPersistedNoteRef.current,
          );
        }
      }
    };

    const debouncedUpdateOrderNote = useDebouncedCallback(
      updateOrderNote,
      INPUT_DEBOUNCE_DELAY_MS,
    );

    // A note typed within the debounce window right before the component
    // unmounts (Continue, switching inbounds) would otherwise be lost.
    useEffect(
      () => () => debouncedUpdateOrderNote.flush(),
      [debouncedUpdateOrderNote],
    );

    const onSaveOrderNote = (newOrderNote: string) => {
      setAdditionalInputValue(
        OrderSummaryInputID.Notes,
        i18n.t('order_notes'),
        newOrderNote,
      );
      debouncedUpdateOrderNote(newOrderNote);
    };

    useEffect(() => {
      if (isCorrectionMode) {
        return;
      }

      // Right now we only set the PO number and order note for the first
      // order. This is needed so that if a value exists, it is properly
      // passed through to the order summary panel on initial load.
      if (isInitialMountRef.current) {
        setAdditionalInputValue(
          OrderSummaryInputID.PONumber,
          i18n.t('po_number'),
          poNumber,
        );

        if (orderNoteInputConfig) {
          setAdditionalInputValue(
            OrderSummaryInputID.Notes,
            i18n.t('order_notes'),
            orderNotes,
          );
        }

        initialPoNumber.current = poNumber;
        isInitialMountRef.current = false;
      }
    }, [
      isCorrectionMode,
      orderNoteInputConfig,
      orderNotes,
      poNumber,
      setAdditionalInputValue,
    ]);

    const validateInputs = useCallback((): InputErrorMap => {
      const nextInputErrorMap: InputErrorMap = {};

      for (const inputConfig of [poNumberInputConfig, orderNoteInputConfig]) {
        if (!inputConfig) {
          continue;
        }

        const inputValue = (
          additionalInputValueMap[inputConfig.id]?.value ?? ''
        ).trim();

        const error = getOrderSummaryInputError(inputConfig, inputValue);
        if (error) {
          nextInputErrorMap[inputConfig.id] = error;
        }
      }

      setAdditionalInputErrorMap(nextInputErrorMap);
      return nextInputErrorMap;
    }, [
      poNumberInputConfig,
      orderNoteInputConfig,
      additionalInputValueMap,
      setAdditionalInputErrorMap,
    ]);

    // The selected fulfillment is seeded from the current supplier availability,
    // which falls back to the next available day when the requested date is no
    // longer orderable. That is right for an order still being placed, but a
    // submitted order has to show the date it was actually submitted for, so
    // read it off the smart order instead.
    const smartOrderFulfillment =
      useMemo((): SelectedFulfillmentType | null => {
        const fulfillmentAt = orderMetadata[0]?.fulfillmentAt;

        if (!fulfillmentAt) {
          return null;
        }

        return {
          date: adjustMomentForTimezone(
            fulfillmentAt,
            restaurantTimezone,
          ).toISOString(),
          fulfillmentSettingsUUID: null,
          timeslot: null,
          type: (orderMetadata[0].fulfillmentType ||
            FulfillmentName.Delivery) as FulfillmentName,
        };
      }, [orderMetadata, restaurantTimezone]);

    const displayFulfillment = isCorrectionMode
      ? smartOrderFulfillment
      : selectedFulfillment;

    const fulfillmentDisplayName = orderAvailability.fulfillmentDisplayName;
    const fulfillmentDateInputText: string = displayFulfillment
      ? translateFulfillmentType(
          displayFulfillment.type || FulfillmentName.Delivery,
          'inbound_detail_list.fulfillment_type',
          fulfillmentDisplayName,
          {
            date: formatDate(displayFulfillment.date, {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            }),
          },
        )
      : '';

    const poNumberInputLabel = customPOInput?.title
      ? getTranslationValue(customPOInput.title)
      : i18n.t('inbound_detail_list.po_number_input_label');

    const orderNoteInputLabel = customNoteInput?.title
      ? getTranslationValue(customNoteInput.title)
      : i18n.t('inbound_detail_list.order_note_input_label');

    useImperativeHandle(
      ref,
      () => ({
        blur: () => {
          poNumberInputRef.current?.blur();
          noteInputRef.current?.blur();
        },
        scrollToSelf: () => {
          onScrollToSelf?.();
        },
        validateAndFocus: () => {
          const inputErrorMap = validateInputs();
          if (inputErrorMap[OrderSummaryInputID.PONumber]) {
            poNumberInputRef.current?.focus();
          } else if (inputErrorMap[OrderSummaryInputID.Notes]) {
            noteInputRef.current?.focus();
          }
          return Object.keys(inputErrorMap).length === 0;
        },
      }),
      [validateInputs, onScrollToSelf],
    );

    const doesNeedToVerifySelectedFulfillmentDate = useMemo<boolean>(() => {
      if (orderMetadata.length === 0) {
        return false;
      }

      const order = orderMetadata[0];
      const displayFulfillmentDate = displayFulfillment?.date;

      if (!order.originalFulfillmentAt || !displayFulfillmentDate) {
        return false;
      }

      // displayFulfillmentDate is stored as timezone-aware UTC timestamp
      // representing start of day in RESTAURANT timezone
      // parse as UTC, then convert to restaurant timezone to account for
      // timezone offset before extracting the date
      const selectedDateInRestaurantTz = moment
        .utc(displayFulfillmentDate)
        .tz(restaurantTimezone);
      const selectedYMD = selectedDateInRestaurantTz.format('YYYY-MM-DD');

      // originalFulfillmentAt is stored as naive UTC, get YYYY-MM-DD directly
      const originalDateUtc = moment.utc(order.originalFulfillmentAt);
      const originalYMD = originalDateUtc.format('YYYY-MM-DD');

      return selectedYMD !== originalYMD;
    }, [orderMetadata, displayFulfillment, restaurantTimezone]);

    const originalFulfillmentDateFormatted: string | null = useMemo(() => {
      if (orderMetadata.length === 0) {
        return null;
      }

      const order = orderMetadata[0];

      if (!Boolean(order.originalFulfillmentAt)) {
        return null;
      }

      const normalizedDate = adjustMomentForTimezone(
        order.originalFulfillmentAt!,
        restaurantTimezone,
      );

      return formatDateWithYear(normalizedDate);
    }, [orderMetadata, restaurantTimezone]);

    return (
      <Pressable
        onPress={() => {
          poNumberInputRef.current?.blur();
          noteInputRef.current?.blur();
        }}
        style={[styles.container, containerStyle]}
      >
        {accountPickerProps ? (
          <InboundDetailListCustomerAccountPicker
            {...accountPickerProps}
            disabled={Boolean(accountPickerProps.disabled) || isViewOnly}
          />
        ) : null}

        <View style={styles.fieldsWrap}>
          <View style={[styles.fulfillmentDateContainer, styles.fieldColumn]}>
            <Pressable
              disabled={isViewOnly}
              onPress={() => {
                if (!isUpdatingInboundFulfillmentOptions) {
                  setShowFulfillmentSelector(true);
                }
              }}
              testID="fulfillment-date-pressable"
            >
              <SkTypography bold marginBottom={8} size="$sm">
                {i18n.t('inbound_detail_list.fulfillment_date_input_label')}
              </SkTypography>

              <View
                ref={fulfillmentDateInputRef}
                style={[!isViewOnly ? styles.pointer : null, styles.field]}
                testID="fulfillment-date-input"
              >
                <View style={styles.fieldText}>
                  <SkTypography numberOfLines={1} size="$sm" variant="body">
                    {fulfillmentDateInputText}
                  </SkTypography>
                </View>

                {isViewOnly ? null : (
                  <View style={styles.icon}>
                    <ChevronDownThin
                      size={20}
                      stroke={theme.sk.color.icon.primary}
                      testID="fulfillment-date-chevron"
                    />
                  </View>
                )}
              </View>
            </Pressable>

            {doesNeedToVerifySelectedFulfillmentDate &&
            Boolean(originalFulfillmentDateFormatted) ? (
              <View style={styles.fulfillmentDateWarningTextContainer}>
                <AlertIcon
                  fill={theme.sk.color.text.warning}
                  height={16}
                  width={16}
                />

                <Typography
                  color={theme.sk.color.text.warning}
                  variant="paragraph-sm"
                >
                  {i18n.t('inbound_detail_list.date_changed_warning', {
                    date: originalFulfillmentDateFormatted,
                  })}
                </Typography>
              </View>
            ) : null}
          </View>

          {poNumberInputConfig ? (
            <View collapsable={false} style={styles.fieldColumn}>
              <SkTypography bold marginBottom={8} size="$sm">
                {poNumberInputLabel}
              </SkTypography>

              <TextInput
                containerStyle={styles.fieldInput}
                disabledStyle={styles.fieldInputDisabled}
                editable={!Boolean(isViewOnly)}
                error={additionalInputErrorMap[OrderSummaryInputID.PONumber]}
                inDraggableSheet={isSmallLayout}
                inputRef={poNumberInputRef}
                onBlur={validateInputs}
                onChangeText={onSavePONumber}
                style={styles.fieldInputText}
                testID="fulfillment-date-po-number-input"
                value={
                  isCorrectionMode
                    ? poNumber
                    : (additionalInputValueMap[OrderSummaryInputID.PONumber]
                        ?.value ?? '')
                }
              />
            </View>
          ) : null}
        </View>

        {orderNoteInputConfig ? (
          <View collapsable={false}>
            <SkTypography bold marginBottom={8} size="$sm">
              {orderNoteInputLabel}
            </SkTypography>

            <TextInput
              containerStyle={styles.fieldInput}
              disabledStyle={styles.fieldInputDisabled}
              editable={!Boolean(isViewOnly)}
              error={additionalInputErrorMap[OrderSummaryInputID.Notes]}
              inDraggableSheet={isSmallLayout}
              inputRef={noteInputRef}
              onBlur={validateInputs}
              onChangeText={onSaveOrderNote}
              style={styles.fieldInputText}
              testID="fulfillment-date-order-note-input"
              value={
                isCorrectionMode
                  ? (orderMetadata[0].submittedOrderNote ?? '')
                  : (additionalInputValueMap[OrderSummaryInputID.Notes]
                      ?.value ?? '')
              }
            />
          </View>
        ) : null}

        {selectedFulfillment && showFulfillmentSelector ? (
          <FulfillmentActionSheet
            draftOrder={null}
            fulfillmentOptions={selectableFulfillmentOptions}
            initialSelectedFulfillment={selectedFulfillment}
            isLoading={isUpdatingQuantity}
            onClose={() => setShowFulfillmentSelector(false)}
            onSave={async (newFulfillment) => {
              await onSaveFulfillment(newFulfillment);
            }}
            popoverTarget={fulfillmentDateInputRef.current}
            renderAsPopover
            trackingProperties={{
              smartOrderUUID: orderMetadata.map(
                (order) => order.smartOrderUUID,
              ),
              source: 'inbound_detail_list_order_options',
            }}
          />
        ) : null}
      </Pressable>
    );
  },
);

const useStyles = createStyles((theme) => ({
  container: {
    cursor: 'default' as CursorValue,
    gap: 16,
  },
  field: {
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    borderColor: theme.sk.color.border.input.default,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    flexDirection: 'row',
    gap: 8,
    height: 40,
    // Vertical padding would clip the text at this fixed height.
    paddingHorizontal: theme.sk.spacing.spaceSMedium,
  },
  fieldColumn: {
    flexBasis: FIELD_MIN_BASIS,
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 0,
  },
  fieldInput: {
    borderColor: theme.sk.color.border.input.default,
    borderRadius: theme.sk.radii.radiusMedium,
    height: 40,
  },
  fieldInputDisabled: {
    color: theme.colors.primary,
  },
  fieldInputText: {
    fontFamily: theme.sk.typography.input.md.fontFamily,
    fontSize: theme.sk.typography.input.md.fontSize,
    letterSpacing: theme.sk.typography.input.md.letterSpacing,
    paddingHorizontal: theme.sk.spacing.spaceSMedium,
  },
  fieldsWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  fieldText: {
    flex: 1,
  },
  fulfillmentDateContainer: {
    gap: 4,
  },
  fulfillmentDateWarningTextContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
  },
  icon: {
    alignItems: 'center',
    height: 24,
    justifyContent: 'center',
    width: 24,
  },
  pointer: {
    cursor: 'pointer',
  },
}));
